import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import RestaurantSelector from './components/RestaurantSelector';
import DateTimePicker from './components/DateTimePicker';
import ReservationForm from './components/ReservationForm';
import ConfirmationModal from './components/ConfirmationModal';
import Icon from '../../components/AppIcon';

const TableReservation = () => {
  const navigate = useNavigate();
  const [cartCount] = useState(3);
  const [currentStep, setCurrentStep] = useState(1);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [reservationData, setReservationData] = useState({
    guestCount: 2,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    specialRequests: '',
    seatingPreference: 'no-preference',
    accessibilityNeeds: false
  });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [loading, setLoading] = useState(false);

  // Mock shipping locations data
  const restaurants = [
  {
    id: 'north-america',
    name: 'North America Hub',
    address: '123 Logistics Parkway, New York, NY 10001',
    hours: 'Mon-Sun: 24/7 Operations',
    capacity: 5000,
    image: "https://images.unsplash.com/photo-1566576721346-d4a3b4eaeb55",
    imageAlt: 'Modern logistics warehouse facility with organized shipping operations in North America',
    phone: '+1 (555) 123-4567',
    features: ['Air Freight', 'Ocean Freight', 'Express Delivery']
  },
  {
    id: 'europe',
    name: 'European Distribution Center',
    address: '456 Cargo Avenue, Rotterdam, Netherlands',
    hours: 'Mon-Sun: 24/7 Operations',
    capacity: 4500,
    image: "https://images.unsplash.com/photo-1601584115197-04ecc0da31d7",
    imageAlt: 'Large European distribution center with modern freight handling equipment',
    phone: '+31 10 123 4567',
    features: ['EU Customs', 'Road Freight', 'Warehousing']
  },
  {
    id: 'asia-pacific',
    name: 'Asia-Pacific Gateway',
    address: '789 Port Road, Singapore 018956',
    hours: 'Mon-Sun: 24/7 Operations',
    capacity: 6000,
    image: "https://images.unsplash.com/photo-1578575437130-527eed3abbec",
    imageAlt: 'Busy Asian port facility with container ships and cargo handling operations',
    phone: '+65 6123 4567',
    features: ['Container Shipping', 'Air Cargo', 'Cross-Border']
  },
  {
    id: 'middle-east',
    name: 'Middle East Logistics Hub',
    address: '321 Trade Zone, Dubai, UAE',
    hours: 'Mon-Sun: 24/7 Operations',
    capacity: 3500,
    image: "https://images.unsplash.com/photo-1553413077-190dd305871c",
    imageAlt: 'State-of-the-art Middle Eastern logistics hub with climate-controlled storage',
    phone: '+971 4 123 4567',
    features: ['Free Zone', 'Temperature Control', 'Regional Distribution']
  },
  {
    id: 'south-america',
    name: 'South America Operations',
    address: '654 Industrial Boulevard, São Paulo, Brazil',
    hours: 'Mon-Sun: 24/7 Operations',
    capacity: 2800,
    image: "https://images.unsplash.com/photo-1605732562742-3023a888e56e",
    imageAlt: 'Modern South American freight terminal with organized pallet storage',
    phone: '+55 11 1234 5678',
    features: ['Import/Export', 'Ground Transport', 'Customs Brokerage']
  },
  {
    id: 'africa',
    name: 'African Trade Center',
    address: '987 Commerce Street, Johannesburg, South Africa',
    hours: 'Mon-Sat: 6:00 AM - 10:00 PM',
    capacity: 2000,
    image: "https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d",
    imageAlt: 'African logistics center with efficient cargo handling and distribution services',
    phone: '+27 11 123 4567',
    features: ['Regional Network', 'Last-Mile Delivery', 'Secure Storage']
  }];


  const handleRestaurantSelect = (restaurant) => {
    setSelectedRestaurant(restaurant);
    setCurrentStep(2);
  };

  const handleDateTimeSelect = (date, time) => {
    setSelectedDate(date);
    setSelectedTime(time);
    setCurrentStep(3);
  };

  const handleReservationSubmit = async (formData) => {
    setLoading(true);
    setReservationData(formData);

    // Simulate API call for shipment booking
    await new Promise((resolve) => setTimeout(resolve, 2000));

    setLoading(false);
    setShowConfirmation(true);
  };

  const handleConfirmationClose = () => {
    setShowConfirmation(false);
    // Reset form or navigate to another page
    setCurrentStep(1);
    setSelectedRestaurant(null);
    setSelectedDate(null);
    setSelectedTime(null);
  };

  const handleCartClick = () => {
    navigate('/shopping-cart');
  };

  const handleAccountClick = (action) => {
    if (action === 'login') navigate('/login');
    if (action === 'register') navigate('/register');
    if (action === 'account') navigate('/user-account');
  };

  const renderStepIndicator = () =>
  <div className="flex items-center justify-center space-x-4 mb-8">
      {[1, 2, 3]?.map((step) =>
    <div key={step} className="flex items-center">
          <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all duration-200 ${
      step <= currentStep ?
      'bg-primary border-primary text-primary-foreground' :
      'bg-card border-border text-muted-foreground'}`
      }>
            {step < currentStep ?
        <Icon name="Check" size={16} /> :

        <span className="font-body font-medium">{step}</span>
        }
          </div>
          {step < 3 &&
      <div className={`w-16 h-0.5 mx-2 transition-colors duration-200 ${
      step < currentStep ? 'bg-primary' : 'bg-border'}`
      } />
      }
        </div>
    )}
    </div>;


  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <RestaurantSelector
            restaurants={restaurants}
            onSelect={handleRestaurantSelect} />);


      case 2:
        return (
          <DateTimePicker
            restaurant={selectedRestaurant}
            onSelect={handleDateTimeSelect}
            onBack={() => setCurrentStep(1)} />);


      case 3:
        return (
          <ReservationForm
            restaurant={selectedRestaurant}
            date={selectedDate}
            time={selectedTime}
            onSubmit={handleReservationSubmit}
            onBack={() => setCurrentStep(2)}
            loading={loading} />);


      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <Header
        cartCount={cartCount}
        onCartClick={handleCartClick}
        onAccountClick={handleAccountClick}
        onSearch={() => {}}
        onLogout={() => {}} />


      {/* Main Content */}
      <main className="pt-16">
        {/* Hero Section */}
        <section className="bg-primary text-primary-foreground py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center">
              <h1 className="text-3xl lg:text-5xl font-heading font-bold mb-4">
                Reserve Your Table
              </h1>
              <p className="text-lg lg:text-xl font-body opacity-90 max-w-2xl mx-auto">
                Book your perfect dining experience at any of our Pesto locations. Choose your preferred restaurant, date, and time for an unforgettable meal.
              </p>
            </div>
          </div>
        </section>

        {/* Reservation Content */}
        <section className="py-8 lg:py-12">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {/* Step Indicator */}
            {renderStepIndicator()}

            {/* Step Content */}
            <div className="bg-card rounded-2xl p-6 lg:p-8 shadow-warm">
              {renderStepContent()}
            </div>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="bg-muted py-12 lg:py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="bg-card rounded-2xl p-8 lg:p-12 shadow-warm">
              <div className="w-16 h-16 bg-accent rounded-full flex items-center justify-center mx-auto mb-6">
                <Icon name="Calendar" size={24} color="#4C1D0A" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-heading font-bold text-foreground mb-4">
                Need Help with Your Reservation?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
                Our team is here to assist you with special requests, large parties, or any questions about your dining experience. Call us directly for personalized service.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+15551234567"
                  className="inline-flex items-center space-x-2 px-8 py-3 bg-primary text-primary-foreground rounded-lg font-body font-medium hover:bg-primary/90 transition-all duration-200 hover:scale-105">
                  <Icon name="Phone" size={18} />
                  <span>Call (555) 123-4567</span>
                </a>
                <button
                  onClick={() => navigate('/menu-catalog')}
                  className="inline-flex items-center space-x-2 px-8 py-3 bg-accent text-accent-foreground rounded-lg font-body font-medium hover:bg-accent/90 transition-all duration-200 hover:scale-105">
                  <Icon name="Menu" size={18} />
                  <span>View Menu</span>
                </button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-secondary text-secondary-foreground py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="flex items-center space-x-2 mb-4 md:mb-0">
              <div className="w-8 h-8 bg-primary rounded-lg flex items-center justify-center">
                <Icon name="UtensilsCrossed" size={20} color="white" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-lg">Pesto</h3>
                <p className="text-xs opacity-80 -mt-1">Restaurant</p>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm opacity-80">
                © {new Date()?.getFullYear()} Pesto Restaurant. All rights reserved.
              </p>
              <p className="text-xs opacity-60 mt-1">
                Made with ❤️ for food lovers everywhere
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* Confirmation Modal */}
      {showConfirmation &&
      <ConfirmationModal
        restaurant={selectedRestaurant}
        date={selectedDate}
        time={selectedTime}
        reservationData={reservationData}
        onClose={handleConfirmationClose} />

      }
    </div>);

};

export default TableReservation;